const normalizeUnit = (unit) => {
  const value = String(unit || "").toLowerCase();

  if (value === "g" || value === "gr") return "gram";
  if (value === "liter") return "l";

  return value;
};

const findRawUnitMeta = (units = [], unitValue = "") => {
  const normalized = normalizeUnit(unitValue);

  return units?.find((unit) => normalizeUnit(unit?.value) === normalized);
};

export const getUnitMeta = (units = [], unitValue = "") => {
  const normalized = normalizeUnit(unitValue);
  const fromMaster = findRawUnitMeta(units, normalized);

  if (fromMaster) {
    return {
      ...fromMaster,
      factor: Number(fromMaster?.factor || 1),
      base_unit_value: normalizeUnit(fromMaster?.base_unit_value || fromMaster?.value || unitValue),
    };
  }

  const fallback = {
    gram: { value: "gram", factor: 1, base_unit_value: "gram", base_unit_label: "gr" },
    kg: { value: "kg", factor: 1000, base_unit_value: "gram", base_unit_label: "gr" },
    ml: { value: "ml", factor: 1, base_unit_value: "ml", base_unit_label: "ml" },
    l: { value: "l", factor: 1000, base_unit_value: "ml", base_unit_label: "ml" },
    pcs: { value: "pcs", factor: 1, base_unit_value: "pcs", base_unit_label: "pcs" },
  };

  return fallback[normalized] || { value: normalized || "pcs", factor: 1, base_unit_value: normalized || "pcs" };
};

export const getBaseUnitValue = (units = [], unitValue = "") => {
  let current = normalizeUnit(unitValue || "pcs");
  const visited = new Set();

  while (current && !visited.has(current)) {
    visited.add(current);
    const rawMeta = findRawUnitMeta(units, current);
    const next = normalizeUnit(rawMeta?.base_unit_value || rawMeta?.value || current);

    if (!next || next === current) return current;
    current = next;
  }

  return current || normalizeUnit(getUnitMeta(units, unitValue)?.base_unit_value || unitValue || "pcs");
};

export const getUnitFactorToBase = (units = [], unitValue = "") => {
  let current = normalizeUnit(unitValue || "pcs");
  let factor = 1;
  let found = false;
  const visited = new Set();

  while (current && !visited.has(current)) {
    visited.add(current);
    const rawMeta = findRawUnitMeta(units, current);

    if (!rawMeta) break;

    found = true;
    factor *= Math.max(Number.MIN_VALUE, Number(rawMeta?.factor || 1));
    const next = normalizeUnit(rawMeta?.base_unit_value || rawMeta?.value || current);

    if (!next || next === current) break;
    current = next;
  }

  return found ? factor : Number(getUnitMeta(units, unitValue)?.factor || 1);
};

export const getBaseUnitLabel = (units = [], unitValue = "") => {
  const baseUnit = getBaseUnitValue(units, unitValue);
  const baseMeta = getUnitMeta(units, baseUnit);

  return baseMeta?.label || { gram: "gram", ml: "ml", pcs: "pcs" }[baseUnit] || baseUnit;
};

export const getPriceUnitLabel = (units = [], unitValue = "") => {
  const baseUnit = getBaseUnitValue(units, unitValue);

  if (baseUnit === "gram") return "kg";
  if (baseUnit === "ml") return "L";

  return getUnitMeta(units, baseUnit)?.label || baseUnit || "unit";
};

export const isWeightOrVolumeUnit = (unit, units = []) => ["gram", "ml"].includes(getBaseUnitValue(units, unit));

export const calculateBaseQuantity = (item = {}, units = []) => {
  const qty = Math.max(0, Number(item?.qty || 0));
  const factor = getUnitFactorToBase(units, item?.unit);

  if (!isWeightOrVolumeUnit(item?.unit, units)) return qty * factor;

  const weight = Math.max(0, Number(item?.weight || 0));

  return weight * qty * factor;
};

export const getLatestUnitCost = (item = {}) =>
  Number(item?.price_trend?.current_price || item?.unit_cost || item?.last_unit_cost || 0);

export const calculateIngredientCost = (ingredient = {}, units = []) => {
  const unit = ingredient?.unit;
  const unitCost = Number(ingredient?.unit_cost ?? getLatestUnitCost(ingredient));
  const qty = Math.max(0, Number(ingredient?.qty ?? 1));

  if (isWeightOrVolumeUnit(unit, units)) {
    return Math.round(unitCost * (calculateBaseQuantity({ ...ingredient, qty }, units) / 1000));
  }

  return Math.round(unitCost * calculateBaseQuantity({ ...ingredient, qty }, units));
};

export const calculateRecipeCogs = (recipeMaterials = [], allMaterials = [], units = []) =>
  (recipeMaterials || []).reduce((total, recipe) => {
    const currentMaterial =
      allMaterials?.find((material) => String(material.id) === String(recipe.material_id || recipe.id)) || {};
    const ingredient = {
      ...currentMaterial,
      ...recipe,
      unit: recipe?.unit || currentMaterial?.unit,
      unit_cost: getLatestUnitCost(currentMaterial) || Number(recipe?.unit_cost || 0),
    };

    return total + calculateIngredientCost(ingredient, units);
  }, 0);

export const calculateMarginPercent = (price, cogs) => {
  const currentPrice = Number(price || 0);
  const currentCogs = Number(cogs || 0);

  if (currentPrice <= 0) return 0;

  return ((currentPrice - currentCogs) / currentPrice) * 100;
};

export const roundSellingPrice = (value, step = 500) => Math.ceil(Number(value || 0) / step) * step;

export const buildRecommendedPrices = (cogs, margins = [35, 45, 55]) =>
  margins
    .map((margin) => {
      const divisor = 1 - Number(margin || 0) / 100;

      return {
        margin,
        label: margin === 35 ? "Aman" : margin === 45 ? "Ideal" : "Premium",
        hint: `Margin ${margin}%`,
        value: divisor > 0 ? roundSellingPrice(Number(cogs || 0) / divisor) : 0,
      };
    })
    .filter((item) => item.value > 0);
